import { PDPSectionType } from '@graphql/generated';
import { useCatalogRegistryTranslations } from '../../Catalog.i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCatalogRegistryTelemetry } from '../../Catalog.telemetry';
import { getCurationNavigationState, useCatalogContext } from '../../state/Catalog';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useProductImageGallery } from './components/ProductImageGallery/useProductImageGallery';
import { RegistryItemSource, UseSharedCatalogMutationReturn } from '../../Catalog.controller';
import { deriveTelemetryPage } from '../../utils/deriveTelemetryPage';
import { ExtraArgsByType } from '../PdpSections/Sections/PdpSection/PdpSections.utils';
import { CatalogCollectionProductItemDetails, MinimalCatalogProductItemDetails } from '../../Catalog.types';
import { useCatalogRouting } from '@apps/registry/common/components/Catalog/useCatalogRouting';
import { useCatalogLoggingContext } from '../../Catalog.logging';
import { useManagedSearchParam } from '@shared/hooks/useManagedSearchParam';
import { useMount } from '@shared/utils/hooks/useMount';

export interface Tab {
  label: string;
  description: string;
}

export interface ProductDetailsControllerArgs {
  product: Maybe<MinimalCatalogProductItemDetails>;
  addCatalogItem?: UseSharedCatalogMutationReturn['addCatalogItemToRegistry'];
  isDialog: boolean;
}

export type UseProductDetailsControllerReturn = ReturnType<typeof useProductDetailsController>;

export const useProductDetailsController = ({ product, addCatalogItem, isDialog }: ProductDetailsControllerArgs) => {
  const [productSelected, setProductSelected] = useState(product);

  const [, setProductRecSearchParam] = useManagedSearchParam('productRec');
  const [, setChecklistSearchParam] = useManagedSearchParam('checklist');

  const { getProductDetailPageTranslations } = useCatalogRegistryTranslations();
  const { whyWeLoveIt, description } = getProductDetailPageTranslations();

  const { selectedCategory, goToCategory, goToCurationDetails, goToCatalogItem, goToRootCatalog, isMobile, onOpenEditGiftDialog } = useCatalogContext();
  const { productDetail, productDetailModal } = useCatalogRegistryTelemetry();
  const { getProductCatalogCategoryBasePageEventProps, trackCategoryNavigateAction } = useCatalogLoggingContext();
  const { relatedProductCollectionsClicked } = useCatalogRegistryTelemetry();
  const curationFromStorage = getCurationNavigationState();

  const telemetryPageValue = deriveTelemetryPage({
    selectedCategory,
    product: product,
    view: isDialog ? 'pdpModal' : 'pdp',
  });

  const storedCuration = useMemo(() => {
    return curationFromStorage && product && curationFromStorage.children.some(id => id === product.id) ? curationFromStorage : null;
  }, [product, curationFromStorage]);

  const { handleOnClickMediaAsset, mediaAssets, selectedMediaAsset } = useProductImageGallery({
    initialMediaAsset: productSelected?.mediaAssets[0],
    selectedProduct: productSelected,
  });

  const trackPage = useEventCallback((action: 'enter', catalogItemId: string) => {
    const pageFn = isDialog ? productDetailModal : productDetail;
    pageFn[action]({
      ...getProductCatalogCategoryBasePageEventProps({ product }).categoryTelemArgs,
      storefrontStatus: product?.storefrontInfo?.storefrontStatus,
      productCatalogItemId: catalogItemId,
    });
  });
  const productId = product?.id;

  useEffect(() => {
    if (productId) {
      trackPage('enter', productId);
    }
  }, [trackPage, productId]);

  /**
   * Clear known search params when unmounting
   */
  useMount(() => {
    return () => {
      setProductRecSearchParam(null);
      setChecklistSearchParam(null);
    };
  });

  const createProductTabs = () => {
    const productInfo = [
      { label: whyWeLoveIt, description: product?.whyWeLoveIt },
      { label: description, description: product?.shortDescription },
    ];
    const tabItems = productInfo.filter(tab => {
      return tab.description?.length ? true : false;
    });
    return tabItems as Tab[];
  };

  const handleClickBackLink = useEventCallback(() => {
    if (storedCuration && storedCuration.publicUrlPath) {
      trackCategoryNavigateAction({
        category: storedCuration,
        navigationSource: 'breadcrumbs',
        overrides: { page: telemetryPageValue },
      });
      goToCurationDetails(storedCuration.publicUrlPath);
    } else if (selectedCategory) {
      trackCategoryNavigateAction({
        category: selectedCategory,
        navigationSource: 'breadcrumbs',
        overrides: { page: telemetryPageValue },
      });
      goToCategory(selectedCategory.publicUrlPath);
    } else {
      trackCategoryNavigateAction({
        category: null,
        navigationSource: 'breadcrumbs',
        overrides: { page: telemetryPageValue },
      });
      goToRootCatalog();
    }
  });

  useEffect(() => {
    // Update selected when URL has changed
    setProductSelected(product);
  }, [product]);

  const handleViewDetailsCollectionItem = useCallback(
    (_product: CatalogCollectionProductItemDetails) => {
      if (_product) {
        relatedProductCollectionsClicked({ productId: _product.id, productName: _product.title });
        goToCatalogItem(_product.id);
      }
    },
    [goToCatalogItem, relatedProductCollectionsClicked],
  );

  const handleQuickAddCollectionItem = useCallback(
    (_product: CatalogCollectionProductItemDetails, onAddComplete, sectionType: Maybe<PDPSectionType>) => {
      if (_product) {
        const hasDimensions: boolean = !!_product.group?.groupDimensions.length;
        if (hasDimensions) {
          // If the item has dimensions then show item details
          handleViewDetailsCollectionItem(_product);
        } else {
          // If the item doesn't have dimensions then add it
          const component =
            sectionType === PDPSectionType.relatedProductCollections
              ? 'relatedProductsQuickAdd'
              : sectionType === PDPSectionType.productFamilyCollections
                ? 'productFamilyQuickAdd'
                : 'quickAdd';
          const metadataSource = component === 'quickAdd' ? RegistryItemSource.catalogShop : RegistryItemSource.recommendedCatalogShop;
          addCatalogItem?.({
            source: isDialog ? 'pdpModal' : 'pdp',
            payload: {
              qty: 1,
              product: _product,
              metadata: {
                source: metadataSource,
                sourceMetadata: {
                  scannedPrice: _product.price?.floatingPointDecimalString,
                },
              },
              telemetryArgs: {
                method: 'quick add',
                component,
                page: telemetryPageValue,
              },
              onAddComplete,
            },
          });
        }
      }
    },
    [isDialog, addCatalogItem, telemetryPageValue, handleViewDetailsCollectionItem],
  );

  const getSectionExtraArgsByType = useCallback(
    (sectionType: PDPSectionType): ExtraArgsByType<typeof sectionType> => {
      switch (sectionType) {
        case PDPSectionType.relatedProductCollections:
          return {
            collectionDisplayInfos: product?.collectionDisplayInfos ?? [],
            isMobile,
            isDialog,
            selectedProductId: product?.id,
            handleQuickAddCollectionItem,
            handleViewDetailsCollectionItem,
          };
        default:
          return null;
      }
    },
    [product, isMobile, isDialog, handleQuickAddCollectionItem, handleViewDetailsCollectionItem],
  );

  const brandUrl = productSelected?.brand?.publicUrlPath || '';

  const { goToBrandPage } = useCatalogRouting();

  const handleBrandClick = useCallback(() => {
    goToBrandPage(brandUrl);
  }, [goToBrandPage, brandUrl]);

  return {
    createProductTabs,
    selectedProduct: productSelected,
    dimensionInputTitle: product?.group?.groupDimensions[0]?.title || '',
    selectedCategory,
    goToCategory,
    storedCuration,
    handleClickBackLink,
    isMobile,
    handleOnClickMediaAsset,
    mediaAssets,
    selectedMediaAsset,
    telemetryPageValue,
    setProductSelected,
    getSectionExtraArgsByType,
    handleBrandClick,
    onOpenEditGiftDialog,
  } as const;
};
